import React, { useCallback, useEffect, useRef, useState } from 'react';
import './AttachMenu.css';

/**
 * composer-v2 AttachMenu — Slack-style `+` button + dropdown menu in the
 * bottom-actions row. KAP-308.
 *
 * v1 ships one item ("Upload from your computer") that triggers the
 * existing `openFilePicker` callback. Future epics (Record clip, Poll,
 * Canvas, etc.) append items here without changing the trigger or a11y
 * scaffolding.
 *
 * WAI-ARIA APG menu pattern:
 * - Trigger has `aria-haspopup="menu"` + `aria-expanded`.
 * - Menu has `role="menu"`; items have `role="menuitem"` with roving
 *   `tabIndex` (active item = 0, others = -1).
 * - Open: Enter, Space, or ArrowDown on the trigger moves focus to the
 *   first item.
 * - Inside the menu: ArrowUp/Down wrap-around, Home jumps to first, End
 *   to last, Esc closes and returns focus to the trigger, Tab also
 *   closes (no focus trap — Tab moves to the next composer control).
 * - Outside `mousedown` closes (mirrors `LinkPopover` precedent).
 */

function AttachMenuItem({ icon, label, onActivate, isActive, setActive, registerRef }) {
  const ref = useRef(null);

  useEffect(() => {
    registerRef(ref);
    if (isActive && ref.current) {
      ref.current.focus();
    }
  }, [registerRef, isActive]);

  return (
    <button
      ref={ref}
      type="button"
      role="menuitem"
      className="msg-composer-attach-menu-item"
      tabIndex={isActive ? 0 : -1}
      onClick={onActivate}
      onMouseEnter={setActive}
      onFocus={setActive}
    >
      <span className="msg-composer-attach-menu-item-icon" aria-hidden="true">
        {icon}
      </span>
      <span className="msg-composer-attach-menu-item-label">{label}</span>
    </button>
  );
}

function AttachMenu({ openFilePicker, disabled }) {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const itemRefs = useRef([]);

  // Single item v1. Adding entries here adds menu items; arrow-key
  // navigation auto-extends.
  const items = [
    {
      key: 'upload',
      label: 'Upload from your computer',
      icon: (
        <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" focusable="false">
          <path
            d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
          />
        </svg>
      ),
      onActivate: () => {
        setOpen(false);
        openFilePicker();
      }
    }
  ];

  const closeAndRestoreFocus = useCallback(() => {
    setOpen(false);
    // Defer to let the menu unmount before refocusing — without this,
    // React can race the focus call against the unmount and drop focus
    // onto <body>.
    window.setTimeout(() => {
      if (triggerRef.current) triggerRef.current.focus();
    }, 0);
  }, []);

  const handleTriggerClick = useCallback(() => {
    if (disabled) return;
    setOpen((prev) => !prev);
    setActiveIndex(0);
  }, [disabled]);

  const handleTriggerKeyDown = useCallback(
    (event) => {
      if (disabled) return;
      if (event.key === 'Enter' || event.key === ' ' || event.key === 'ArrowDown') {
        event.preventDefault();
        setOpen(true);
        setActiveIndex(0);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        setOpen(true);
        setActiveIndex(items.length - 1);
      }
    },
    [disabled, items.length]
  );

  const handleMenuKeyDown = useCallback(
    (event) => {
      const last = items.length - 1;
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setActiveIndex((i) => (i >= last ? 0 : i + 1));
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        setActiveIndex((i) => (i <= 0 ? last : i - 1));
      } else if (event.key === 'Home') {
        event.preventDefault();
        setActiveIndex(0);
      } else if (event.key === 'End') {
        event.preventDefault();
        setActiveIndex(last);
      } else if (event.key === 'Escape') {
        event.preventDefault();
        closeAndRestoreFocus();
      } else if (event.key === 'Tab') {
        // Don't trap focus — let Tab move out, but close the menu.
        setOpen(false);
      }
    },
    [items.length, closeAndRestoreFocus]
  );

  // Outside-click closes (mirrors composer-v2/LinkPopover.js:46-55).
  useEffect(() => {
    if (!open) return undefined;
    const handlePointerDown = (event) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [open]);

  const registerRef = useCallback((ref) => {
    if (!itemRefs.current.includes(ref)) itemRefs.current.push(ref);
  }, []);

  return (
    <div className="msg-composer-attach-menu-wrapper">
      <button
        ref={triggerRef}
        type="button"
        className={`msg-composer-attach-menu-trigger${open ? ' is-open' : ''}`}
        onClick={handleTriggerClick}
        onKeyDown={handleTriggerKeyDown}
        disabled={disabled}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Attach a file"
        title="Attach a file"
      >
        <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false">
          <path
            d="M12 5v14M5 12h14"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      </button>
      {open && (
        <div
          ref={menuRef}
          role="menu"
          aria-label="Attach or insert"
          className="msg-composer-attach-menu"
          onKeyDown={handleMenuKeyDown}
        >
          {items.map((item, idx) => (
            <AttachMenuItem
              key={item.key}
              icon={item.icon}
              label={item.label}
              isActive={idx === activeIndex}
              setActive={() => setActiveIndex(idx)}
              onActivate={item.onActivate}
              registerRef={registerRef}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default AttachMenu;
